import { useState } from 'react';

import { Check, ChevronDown, MapPin } from 'lucide-react';

import {
  Button,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/components/ui';
import { useLocationScope } from '@src/context/hooks';


/** Functional top-bar location selector (#170): scopes Dashboard / Inventory /
 * Listings (and the parts export) to one stocking location, or "All locations".
 * Reads/writes the per-seller selection from LocationContext. Hidden when the
 * org has no locations. Visual matches the static M6 chip it replaces. */
function LocationSelector({ seller, className }) {
  const { locationId, setLocationId } = useLocationScope();
  const [open, setOpen] = useState(false);

  const locations = seller?.locations || [];

  if (locations.length === 0) {
    return null;
  }

  const active = locations.find((entry) => entry.id === locationId);
  const label = active ? active.name : 'All locations';

  const options = [{ id: 'all', name: 'All locations' }, ...locations];

  const select = (id) => {
    setLocationId(id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'h-auto items-center gap-[7px] rounded-lg border border-hairline bg-surface-2 px-[11px] py-2',
            'cursor-pointer font-normal hover:bg-surface-2 hover:text-ink',
            className,
          )}
        >
          <MapPin className="size-[15px]! text-primary" />
          <span className="text-table font-semibold text-ink">{label}</span>
          <ChevronDown className="size-3.5! text-ink-subtle" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={6}
        className={cn(
          'flex max-h-72 w-56 flex-col overflow-y-auto rounded-lg border border-hairline',
          'bg-surface-1 p-0 py-1 shadow-xl',
        )}
        role="listbox"
      >
        {options.map((option) => {
          const selected = option.id === 'all' ? !active : option.id === locationId;
          return (
            <Button
              variant="ghost"
              key={option.id}
              aria-selected={selected}
              className={cn(
                'h-auto items-center justify-start gap-2 rounded-none px-3.5 py-2 text-left text-table',
                'hover:bg-surface-2',
                selected ? 'font-semibold text-ink hover:text-ink' : 'font-medium text-ink-muted hover:text-ink-muted',
              )}
              role="option"
              onClick={() => select(option.id)}
            >
              <span className="min-w-0 flex-1 truncate">{option.name}</span>
              {selected ? <Check className="size-3.5! shrink-0 text-primary" strokeWidth={2.5} /> : null}
            </Button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

LocationSelector.displayName = 'LocationSelector';
export default LocationSelector;
